/**
 * Flowering plants and terracotta planters (lifted from lantern-water's
 * `botanical-props.js`): folded opaque leaves and five-petal cupped blooms as
 * vertex-coloured triangles, so window boxes and pots stay legible in the
 * view and its reflection with no alpha overdraw and no texture. Each plant
 * draws from its own seed; the material is the kit's shared `botanical`.
 */
import * as THREE from 'three';

const lcg = (start: number): (() => number) => {
  let seed = start;
  return () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
};

const v = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

/** A plant of `count` stems across `width`, seeded; `trailing` adds four trailing strands (a number: over a pot's lip, that much of their length). */
export const floweringPlant = (parent: THREE.Object3D, material: THREE.Material, width = 1, count = 20, seed = 1, trailing: boolean | number = false): THREE.Mesh => {
  const rand = lcg(seed);
  const positions: number[] = [];
  const colors: number[] = [];
  const col = new THREE.Color();
  const triangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: string, shade = 1): void => {
    col.set(color).multiplyScalar(shade);
    for (const p of [a, b, c]) {
      positions.push(p.x, p.y, p.z);
      colors.push(col.r, col.g, col.b);
    }
  };
  const stem = (a: THREE.Vector3, b: THREE.Vector3, radius = 0.007): void => {
    for (const offset of [v(radius, 0, 0), v(0, 0, radius)]) {
      const aa = a.clone().sub(offset);
      const ab = a.clone().add(offset);
      const ba = b.clone().sub(offset);
      const bb = b.clone().add(offset);
      triangle(aa, ab, bb, '#526d38');
      triangle(aa, bb, ba, '#526d38');
    }
  };
  const leaf = (base: THREE.Vector3, angle: number, length: number, droop = 0.02): void => {
    const along = v(Math.cos(angle), droop / length, Math.sin(angle));
    const across = v(-Math.sin(angle), 0, Math.cos(angle)).multiplyScalar(length * 0.3);
    const middle = base.clone().addScaledVector(along, length * 0.48);
    const tip = base.clone().addScaledVector(along, length);
    const ridge = middle.clone().add(v(0, length * 0.16, 0));
    const left = middle.clone().add(across);
    const right = middle.clone().sub(across);
    const green = ['#527543', '#66854b', '#7f954f', '#416b47'][Math.floor(rand() * 4)] ?? '#527543';
    triangle(base, left, ridge, green, 0.77);
    triangle(left, tip, ridge, green, 0.9);
    triangle(base, ridge, right, green, 1.12);
    triangle(right, ridge, tip, green, 1.02);
  };
  const bloom = (center: THREE.Vector3, radius: number, color: string, phase: number): void => {
    const normal = v((rand() - 0.5) * 0.7, 0.85, 0.35 + rand() * 0.4).normalize();
    const u = v(1, 0, 0).cross(normal).normalize();
    const vv = normal.clone().cross(u);
    const point = (r: number, a: number, h: number): THREE.Vector3 =>
      center
        .clone()
        .addScaledVector(u, Math.cos(a) * r)
        .addScaledVector(vv, Math.sin(a) * r)
        .addScaledVector(normal, h);
    const shades = [0.9, 1.03, 1.08, 1.04, 0.97, 0.88];
    for (let petal = 0; petal < 5; petal += 1) {
      const a = phase + (petal / 5) * Math.PI * 2;
      const outline = [
        point(radius * 0.1, a, 0),
        point(radius * 0.65, a - 0.44, radius * 0.1),
        point(radius * 0.96, a - 0.27, radius * 0.19),
        point(radius, a, radius * 0.17),
        point(radius * 0.96, a + 0.27, radius * 0.19),
        point(radius * 0.65, a + 0.44, radius * 0.1),
      ];
      const crease = point(radius * 0.58, a, radius * 0.13);
      for (let edge = 0; edge < outline.length; edge += 1) {
        const p0 = outline[edge];
        const p1 = outline[(edge + 1) % outline.length];
        if (p0 !== undefined && p1 !== undefined) triangle(p0, p1, crease, color, shades[edge] ?? 1);
      }
    }
    for (let i = 0; i < 6; i += 1) triangle(center.clone().addScaledVector(normal, radius * 0.19), point(radius * 0.2, (i * Math.PI) / 3, radius * 0.13), point(radius * 0.2, ((i + 1) * Math.PI) / 3, radius * 0.13), '#efc369');
  };
  const palette = seed % 3 === 0 ? ['#d297bb', '#eee4ce', '#a6689e'] : seed % 3 === 1 ? ['#cd6477', '#e2979d', '#f2dfbd'] : ['#d9b376', '#f1dcc0', '#b878a3'];
  for (let i = 0; i < count; i += 1) {
    const x = ((i + rand()) / count - 0.5) * width;
    const z = (rand() - 0.5) * 0.33;
    const h = 0.1 + rand() * 0.19;
    const root = v(x * 0.8, -0.06, z * 0.75);
    const top = v(x, h, z);
    stem(root, top);
    for (let j = 0; j < 3; j += 1) leaf(root.clone().lerp(top, 0.15 + j * 0.25), rand() * Math.PI * 2, 0.12 + rand() * 0.09, j === 0 ? -0.035 : 0.035);
    bloom(top, 0.075 + rand() * 0.035, palette[i % palette.length] ?? '#d297bb', rand() * 6.28);
  }
  if (trailing !== false) {
    const isPot = typeof trailing === 'number';
    for (let strand = 0; strand < 4; strand += 1) {
      const startX = (strand / 3 - 0.5) * width * 0.9;
      const radius = (width / 0.78) * 0.46;
      const front = isPot ? Math.sqrt(Math.max(0.01, radius * radius - startX * startX)) : 0.22;
      const lipY = isPot ? (width / 0.78) * 0.12 : 0.02;
      let previous = v(startX, lipY, front - 0.035);
      const length = (0.27 + rand() * 0.3) * (isPot ? trailing : 1);
      for (let k = 1; k <= 6; k += 1) {
        const t = k / 6;
        const next = v(startX + Math.sin(t * 4 + strand) * 0.06, lipY - length * t, front + 0.01 + Math.sin(t * Math.PI * 0.65) * 0.08);
        stem(previous, next, 0.006);
        leaf(next, (k % 2 ? 0 : Math.PI) + 0.3, 0.09 + rand() * 0.06, -0.035);
        previous = next;
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  const plant = new THREE.Mesh(geo, material);
  plant.castShadow = true;
  plant.receiveShadow = true;
  parent.add(plant);
  return plant;
};

/** A terracotta pot with a closed rim, its inner wall, recessed soil and a slip band. */
export const terracottaPlanter = (parent: THREE.Object3D, scale: number, clay: THREE.Material, soil: THREE.Material): void => {
  const profile: readonly (readonly [number, number])[] = [
    [0.27, 0],
    [0.28, 0.055],
    [0.3, 0.07],
    [0.42, 0.57],
    [0.46, 0.59],
    [0.46, 0.69],
    [0.42, 0.72],
    [0.39, 0.7],
    [0.38, 0.62],
    [0.35, 0.51],
  ];
  const pot = new THREE.Mesh(new THREE.LatheGeometry(profile.map(([x, y]) => new THREE.Vector2(x * scale, y * scale)), 16), clay);
  pot.castShadow = true;
  pot.receiveShadow = true;
  parent.add(pot);
  const earth = new THREE.Mesh(new THREE.CylinderGeometry(0.36 * scale, 0.36 * scale, 0.015, 16), soil);
  earth.position.y = 0.52 * scale;
  earth.receiveShadow = true;
  parent.add(earth);
  const band = new THREE.Mesh(new THREE.TorusGeometry(0.418 * scale, 0.012 * scale, 4, 16), clay);
  band.rotation.x = Math.PI / 2;
  band.position.y = 0.565 * scale;
  band.castShadow = true;
  band.receiveShadow = true;
  parent.add(band);
};
